const STORAGE_KEY = 'adhd_utm_v1';

const UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

type UtmField = typeof UTM_FIELDS[number];

export type UtmParams = Partial<Record<UtmField, string>>;

export type AdhdSource = 'sms' | 'web';

const safeGetStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const pickUtmFromSearch = (search: string): UtmParams => {
  const result: UtmParams = {};
  if (!search) return result;
  const params = new URLSearchParams(search);
  UTM_FIELDS.forEach((field) => {
    const value = params.get(field);
    if (value) result[field] = value;
  });
  return result;
};

export const captureUtmFromUrl = (search: string): void => {
  const utm = pickUtmFromSearch(search);
  if (!utm.utm_source) return;

  const storage = safeGetStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(utm));
  } catch {
    // storage quota / disabled — silently ignore, tracking is best-effort
  }
};

export const readUtm = (): UtmParams | null => {
  const storage = safeGetStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;

    const sanitized: UtmParams = {};
    UTM_FIELDS.forEach((field) => {
      const value = (parsed as Record<string, unknown>)[field];
      if (typeof value === 'string' && value.length > 0) {
        sanitized[field] = value;
      }
    });
    return Object.keys(sanitized).length > 0 ? sanitized : null;
  } catch {
    return null;
  }
};

export const resolveSource = (utm?: UtmParams | null): AdhdSource => {
  const effectiveUtm = utm === undefined ? readUtm() : utm;
  return effectiveUtm?.utm_source === 'sms' ? 'sms' : 'web';
};

export const clearUtm = (): void => {
  const storage = safeGetStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort; ignore storage errors
  }
};
